import type { VideoAvatarsOptions } from '@mfc/mfc-video-sdk'
import { SNAPVIDEOSTATE, USERSSTATE } from '.'
import { SnapSize, VideoEvent, VideoType } from '../constants/enums'
import { generateVideoConfig } from '../mfcLogic/generateVideoConfig'
import { getVideoPlayerClass } from '../mfcLogic/videoPlayerReady'
import { getSlugAndId } from './getState'
import { isMemberCam } from './internal/memberVideo'
import { slugToID } from './internal/users'
import { pubSubVideo } from './pubSub'
import { registerSnaps } from './state.timers'

export const startVideo = (idOrSlug: string | number) => {
    const [_, id] = getSlugAndId(idOrSlug)
    const player = SNAPVIDEOSTATE[id]
    player?.instance?.render()
    SNAPVIDEOSTATE[id] = { ...player }
    pubSubVideo.emit(VideoEvent.SNAP + '::' + id, SNAPVIDEOSTATE[id])
}

export const setupSnapVideo = (
    idOrSlug: string | number,
    options: {
        videoID: VideoType
        container: HTMLElement
        hideControls?: boolean
        forceMute?: boolean
        autoplayMutedOverlay?: boolean
        onlyVolumeControls?: boolean
        /** Plain HLS regardless of the viewer's saved stream type */
        forceHls?: boolean
        /** The SDK's video avatar options; none means no clips */
        videoAvatars?: VideoAvatarsOptions
        force?: boolean
    },
    videoReadyCallback: (playing: boolean) => void,
): boolean => {
    // The SDK chunk is loaded on demand, so a tile can request video before
    // it has arrived (or after it failed to load). Callers await
    // videoPlayerReady() first; this bail is the crash guard.
    const Player = getVideoPlayerClass()
    if (!Player) {
        return false
    }

    const {
        videoID,
        container,
        hideControls = false,
        onlyVolumeControls = false,
        forceMute = true,
        forceHls = false,
        videoAvatars,
        force = false,
    } = options
    const [slug, id] = getSlugAndId(idOrSlug)
    const player = SNAPVIDEOSTATE[id]
    registerSnaps(id, SnapSize.large)
    // check and destroy existing only if it's a lower priority type
    // -- Menu > Room > PinnedPm > PinnedRoom > FloatingPreview > ModelTile
    if (!destroySnapVideo(id, videoID, force)) {
        // we ran into a lower priority video wanting to play and no force override
        return false
    }

    if (isMemberCam(id)) return false
    const config = generateVideoConfig(id)
    if (!config) {
        console.error(
            '[setupSnapVideo] failed to generate video config',
            {
                idOrSlug,
                id,
                slug,
                player,
            },
            { ...USERSSTATE.users[slugToID['me']] },
        )
        return false
    }
    // SDK preview mode is HLS-only, muted and controls-free, so hideControls
    // is the right key for it; players that keep controls cannot use it.
    config.preview = hideControls
    // One entry only: the SDK never falls back from an HLS failure (its
    // loadSource only advances the chain on a WebRTC error), so a second
    // tech here would never play.
    if (forceHls) config.techOrder = ['hls']
    config.capLevelToPlayerSize = true
    config.allowControls = !hideControls
    config.videoId = 'fvideo_ff_' + videoID + '_' + slug
    config.mute = forceMute
    config.containerEl = container
    config.forceAutoPlay = true
    config.onlyVolumeControls = onlyVolumeControls
    config.autoplayMutedOverlay = false
    // previews have no settings menu, so there is no way to opt back in
    config.zoom = false
    if (videoAvatars) config.videoAvatars = videoAvatars

    const instance = new Player(config)
    if (!forceMute) {
        instance.setMute(false)
        instance.setVolume(instance.volumeByModel() || 1)
    }
    const newPlayerState = {
        id,
        config,
        container,
        instance,
        videoID,
        playing: false,
        videoReadyCallback,
    }
    SNAPVIDEOSTATE[id] = { ...player, ...newPlayerState }
    pubSubVideo.emit(VideoEvent.SNAP + '::' + id, SNAPVIDEOSTATE[id])
    // The SDK's playback snapshot is the source of truth for `playing`; the
    // instance check guards against a snapshot from a replaced player
    instance.on('playbackstatechange', (_event, playback) => {
        const current = SNAPVIDEOSTATE[id]
        if (current?.instance !== instance) return
        current.playing = playback.playing
        // An ACR clip plays instead of the live feed, so `playing` stays
        // false and the onPlaying callback below never fires: reveal on the
        // clip itself. Never report false when it ends: LiveSnapStream
        // builds a new player on false, and after the 'once' pass the live
        // feed reports playing on its own.
        if (playback.videoAvatar) videoReadyCallback(true)
    })
    instance.setOnPlayingCallback(() => {
        try {
            const player = SNAPVIDEOSTATE[id]
            if (!player?.instance) return
            videoReadyCallback(player.playing)
        } catch (e) {
            console.error('[SnapVideo]', { e, videoID, id, slug })
        }
    })
    return true
}

const destroySnapVideo = (id: number, videoID: VideoType, force = false) => {
    const player = SNAPVIDEOSTATE[id]
    const playerExists = !!(player && player.instance)
    if (!playerExists) return true
    if (player.videoID <= videoID || force) {
        player.videoReadyCallback(false)
        player.instance.Disconnect()
        player.instance = null
        player.container = null
        delete SNAPVIDEOSTATE[id]
        return true
    }

    // unsafe to proceed with starting new video
    return false
}

export const unregisterSnapVideo = (
    idOrSlug: string | number,
    videoID: VideoType,
) => {
    const [_, id] = getSlugAndId(idOrSlug)
    destroySnapVideo(id, videoID)
}
